import { createClient, type SupabaseClient } from '@supabase/supabase-js';

// Browser Supabase client. It only ever holds the publishable key, which is public by design:
// RLS on every exposed table is what protects data. Agent runs, tools, git, GitHub, Jira,
// secrets and approvals stay on the Fastify API — the browser never controls agents directly.

const url = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/$/, '');
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

// A secret key in a VITE_ variable would ship to every browser; refuse to use it.
const looksSecret = !!key && (key.startsWith('sb_secret_') || /service_role/.test(atobSafe(key.split('.')[1] ?? '')));

export const supabase: SupabaseClient | null = url && key && !looksSecret ? createClient(url, key) : null;

export const supabaseBrowserConfig = { url: url ?? null, configured: !!supabase, looksSecret };

/** Browser → Supabase reachability with the publishable key (no data is read). */
export async function checkSupabaseFromBrowser(): Promise<{ ok: boolean; latencyMs: number; message: string }> {
  if (looksSecret) return { ok: false, latencyMs: 0, message: 'VITE_SUPABASE_PUBLISHABLE_KEY holds a secret key. Replace it with the publishable key and rotate the secret.' };
  if (!url || !key) return { ok: false, latencyMs: 0, message: 'Not configured: set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY in .env' };
  const t0 = performance.now();
  try {
    const r = await fetch(`${url}/auth/v1/health`, { headers: { apikey: key } });
    const latencyMs = Math.round(performance.now() - t0);
    return r.ok ? { ok: true, latencyMs, message: 'Connected' } : { ok: false, latencyMs, message: `Supabase answered ${r.status}` };
  } catch (e) {
    return { ok: false, latencyMs: Math.round(performance.now() - t0), message: `Can't reach Supabase: ${(e as Error).message}` };
  }
}

function atobSafe(s: string) {
  try {
    return atob(s.replace(/-/g, '+').replace(/_/g, '/'));
  } catch {
    return '';
  }
}
